import * as NavigationBar from 'expo-navigation-bar';
import {SessionProvider, useSession} from "@/lib/auth/auth";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {GestureHandlerRootView} from "react-native-gesture-handler";
import {Color} from "@/lib/styles";
import {NativeTabs} from "expo-router/unstable-native-tabs";
import {ThemeProvider} from "@react-navigation/core";
import {DarkTheme} from "@react-navigation/native";
import {Platform, Pressable, StyleSheet, Text} from "react-native";
import {SafeAreaProvider} from "react-native-safe-area-context";
import {KeyboardProvider} from "react-native-keyboard-controller";
import {StatusBar} from "expo-status-bar";
import {useEffect} from "react";
import {Href, SplashScreen, useRouter} from "expo-router";
import {TabList, Tabs, TabSlot, TabTrigger} from 'expo-router/ui';
import SpinnerPage from "@/lib/components/SpinnerPage";
import {ActivityProvider, useActivity} from "@/lib/components/ActivityProvider";
import {NotificationProvider} from "@/lib/components/NotificationProvider";
import {getMessaging} from "@react-native-firebase/messaging";
import {ServerSideNotification} from "@/lib/types";
import {EventProvider} from "@/lib/components/EventProvider";
import {ProfileProvider} from "@/lib/components/ProfileProvider";
import {AccountProvider} from "@/lib/components/AccountProvider";
import {GroupProvider} from "@/lib/components/GroupProvider";

SplashScreen.preventAutoHideAsync();

export default function Root() {
    const queryClient = new QueryClient();
    const router = useRouter();
    //const colorScheme = useColorScheme();

    const {isLoading} = useSession();

    useEffect(() => {
        if (Platform.OS === 'android') {
            // Set the navigation bar style
            NavigationBar.setStyle('dark');
        }
    }, []);

    if (!isLoading) {
        SplashScreen.hide();
    }

    try {
        const messaging = getMessaging();
        messaging.getInitialNotification().then((msg) => {
            // console.log("Initial notification", msg);
            if (msg === null) {
                return;
            }
            const notif = msg.data as unknown as ServerSideNotification;
            router.push(notif["eventlista.url"] as Href);
        });
        // console.log("Setting up remote notification handler")
        messaging.setBackgroundMessageHandler(async (msg) => {
            // console.log("Background message", msg);
            const notif = msg.data as unknown as ServerSideNotification;
            router.push(notif["eventlista.url"] as Href);
        })
        messaging.onNotificationOpenedApp((msg) => {
            // console.log("Remote FCM message", msg)
            const notif = msg.data as unknown as ServerSideNotification;
            router.push(notif["eventlista.url"] as Href);
        })
    } catch {
    }

    return (
        <SessionProvider>
            <ThemeProvider value={DarkTheme}>
                <StatusBar style={"light"} hidden={false}/>
                <QueryClientProvider client={queryClient}>
                    <ActivityProvider>
                        <NotificationProvider>
                            <AccountProvider>
                                <ProfileProvider>
                                    <EventProvider>
                                        <GroupProvider>
                                            <SafeAreaProvider>
                                                <KeyboardProvider>
                                                    <GestureHandlerRootView>
                                                        {/*<Stack screenOptions={{headerShown: false}}>*/}
                                                        {isLoading && <SpinnerPage/>}
                                                        {!isLoading && Platform.OS !== "web" && <NativeTabsNavigator/>}
                                                        {!isLoading && Platform.OS === "web" && <WebNavigator/>}
                                                        {/*</Stack>*/}
                                                    </GestureHandlerRootView>
                                                </KeyboardProvider>
                                            </SafeAreaProvider>
                                        </GroupProvider>
                                    </EventProvider>
                                </ProfileProvider>
                            </AccountProvider>
                        </NotificationProvider>
                    </ActivityProvider>
                </QueryClientProvider>
            </ThemeProvider>
        </SessionProvider>
    )
}

function NativeTabsNavigator() {
    const {activities} = useActivity();

    return (
        <NativeTabs backgroundColor={Color.Background}
                    tintColor={Platform.OS === "web" ? Color.Background : Color.PrimaryText}
                    indicatorColor={Color.Faded}
                    backBehavior={"history"}
                    blurEffect={"dark"}
        >
            <NativeTabs.Trigger
                name="e"
                disableTransparentOnScrollEdge={true}
            >
                <NativeTabs.Trigger.Label>Eventer</NativeTabs.Trigger.Label>
                <NativeTabs.Trigger.Icon src={require("../assets/tabicons/event.png")}/>
            </NativeTabs.Trigger>
            <NativeTabs.Trigger
                name="s"
                disableTransparentOnScrollEdge={true}
            >
                <NativeTabs.Trigger.Label>Samfunn</NativeTabs.Trigger.Label>
                <NativeTabs.Trigger.Icon src={require("../assets/tabicons/participants.png")}/>
            </NativeTabs.Trigger>
            <NativeTabs.Trigger
                name="u"
                disableTransparentOnScrollEdge={true}
            >
                <NativeTabs.Trigger.Label>Profil</NativeTabs.Trigger.Label>
                <NativeTabs.Trigger.Icon src={require("../assets/tabicons/account.png")}/>
                {activities.length > 0 &&
                    <NativeTabs.Trigger.Badge>{activities.length.toString()}</NativeTabs.Trigger.Badge>}
            </NativeTabs.Trigger>
        </NativeTabs>
    )
}

function WebNavigator() {
    return (
        <Tabs>
            <TabSlot/>
            <TabList style={styles.tabContainer}>
                <TabTrigger name="e" href="/e" asChild>
                    <TabButton name={"Eventer"}/>
                </TabTrigger>
                <TabTrigger name="s" href="/s" asChild>
                    <TabButton name={"Samfunn"}/>
                </TabTrigger>
                <TabTrigger name="u" href="/u" asChild>
                    <TabButton name={"Profil"}/>
                </TabTrigger>
            </TabList>
        </Tabs>
    );
}

function TabButton(props: any) {
    return (
        <Pressable {...props} >
            <Text
                style={[
                    styles.tabButton,
                    props.isFocused && styles.tabButtonActive,
                ]}
            >
                {props.name}
            </Text>
        </Pressable>
    )
}

export const styles = StyleSheet.create({
    tabContainer: {
        width: "100%",
        position: "absolute",
        top: 54,
        flexDirection: "row",
        justifyContent: "center",
        gap: 8,
    },
    tabButton: {
        height: 36,
        alignItems: "center",
        alignContent: "center",
        paddingHorizontal: 16,
        borderRadius: 6,
        color: Color.PrimaryText,
        backgroundColor: Color.Background,
    },
    tabButtonActive: {
        color: Color.Background,
        backgroundColor: Color.Yellow,
    },
})